import React, { useCallback, useEffect, useMemo, useState } from 'react'
import qs from 'qs'
import { Box, InputAdornment, Stack, Tab, Tabs, TextField } from '@mui/material'
import ReactQRCode from 'react-qr-code'
import { Controller } from 'react-hook-form'
import { useRequest } from 'ahooks'
import { Message } from '@cx/ui'
import useCountDown from '@/hooks/useCountDown'
import { getBindMailCode, getSecurityWechatInfo, getSecurityWechatStatus, postUserPhoneCode } from '@/api/user'
import { SecurityOptions } from '@/types'
import { AuthCodeInput, PasswordInput, Text2, VerifyRes } from './common'

const defaultVerifyTypeLabelMap: Record<string, string> = {
  wechat: '微信扫码',
  phone: '手机号验证码',
  email: '邮箱验证码',
  password: '当前密码',
}

const defaultVerifyTypeHintMap: Record<string, string> = {
  wechat: '请使用已绑定微信扫码完成身份校验',
  phone: '将向当前绑定手机号发送验证码',
  email: '将向当前绑定邮箱发送验证码',
  password: '请输入当前已设置的密码完成身份校验',
}

interface SecurityVerifyPanelProps {
  scene: string
  open: boolean
  options?: SecurityOptions
  verifyType: string
  onVerifyTypeChange: (value: string) => void
  control: any
  errors: any
  trigger: any
  setValue: any
  watch: any
  currentPhone?: string
  currentMail?: string
}

const SecurityVerifyPanel: React.FC<SecurityVerifyPanelProps> = ({
  scene,
  open,
  options,
  verifyType,
  onVerifyTypeChange,
  control,
  errors,
  trigger,
  setValue,
  watch,
  currentPhone,
  currentMail,
}) => {
  const [emailCountDown, startEmail] = useCountDown(`SECURITY_EMAIL_${scene}`)
  const [phoneCountDown, startPhone] = useCountDown(`SECURITY_PHONE_${scene}`)
  const verifyState = watch('verify_state')
  const availableTypes = useMemo(() => options?.available_verify_types || [], [options?.available_verify_types])
  const availableTypeKey = availableTypes.join(',')
  const [wechatState, setWechatState] = useState('')

  const verifyTypeLabelMap = useMemo(() => {
    if (scene === 'change_phone') {
      return { ...defaultVerifyTypeLabelMap, phone: '原手机号验证码', email: '邮箱验证码' }
    }
    if (scene === 'change_email') {
      return { ...defaultVerifyTypeLabelMap, phone: '手机号验证码', email: '原邮箱验证码' }
    }
    return defaultVerifyTypeLabelMap
  }, [scene])

  const verifyTypeHintMap = useMemo(() => {
    if (scene === 'change_phone') {
      return {
        ...defaultVerifyTypeHintMap,
        phone: '将向当前绑定手机号发送验证码',
        email: '将向当前绑定邮箱发送验证码',
      }
    }
    if (scene === 'change_email') {
      return {
        ...defaultVerifyTypeHintMap,
        phone: '将向当前绑定手机号发送验证码',
        email: '将向当前绑定邮箱发送验证码',
      }
    }
    return defaultVerifyTypeHintMap
  }, [scene])

  const clearWechatState = useCallback(
    (clearVerifiedState = true) => {
      if (clearVerifiedState) {
        setValue('verify_state', '')
      }
      setWechatState('')
      sessionStorage.removeItem(`security-wechat-state:${scene}`)
    },
    [scene, setValue],
  )

  useEffect(() => {
    if (!verifyType && availableTypes.length) {
      onVerifyTypeChange(options?.recommended_verify_type || availableTypes[0])
    }
  }, [availableTypeKey, availableTypes, onVerifyTypeChange, options?.recommended_verify_type, verifyType])

  useEffect(() => {
    if (!open) {
      clearWechatState()
    }
  }, [clearWechatState, open])

  const { data: wechatInfo, run: loadWechatInfo } = useRequest(() => getSecurityWechatInfo(scene), {
    manual: true,
  })

  useEffect(() => {
    if (open && verifyType === 'wechat') {
      const savedState = sessionStorage.getItem(`security-wechat-state:${scene}`)
      if (savedState) {
        getSecurityWechatStatus(scene, savedState)
          .then((res) => {
            if (res.verified) {
              setValue('verify_state', savedState)
              setWechatState(savedState)
              return
            }
            clearWechatState()
          })
          .catch(() => {
            clearWechatState()
          })
      } else {
        clearWechatState()
      }
    }
  }, [clearWechatState, open, scene, setValue, verifyType])

  useEffect(() => {
    if (open && verifyType === 'wechat' && !verifyState && !wechatState) {
      loadWechatInfo()
    }
  }, [loadWechatInfo, open, scene, verifyState, verifyType, wechatState])

  useEffect(() => {
    if (wechatInfo?.state) {
      setWechatState(wechatInfo.state)
    }
  }, [wechatInfo?.state])

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined
    if (open && verifyType === 'wechat') {
      timer = setInterval(() => {
        const state = verifyState || wechatState || sessionStorage.getItem(`security-wechat-state:${scene}`)
        if (!state) {
          return
        }
        getSecurityWechatStatus(scene, state)
          .then((res) => {
            if (res.verified) {
              setValue('verify_state', state)
              setWechatState(state)
              sessionStorage.setItem(`security-wechat-state:${scene}`, state)
              clearInterval(timer)
            }
          })
          .catch(() => {})
      }, 2000)
    }
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [open, verifyType, scene, setValue, verifyState, wechatState])

  const wechatAuthUrl = useMemo(() => {
    if (!wechatState || !wechatInfo || wechatInfo.qr_type !== 'oa') {
      return ''
    }
    return `https://open.weixin.qq.com/connect/oauth2/authorize?${qs.stringify(
      {
        appid: wechatInfo.app_id,
        redirect_uri: encodeURI((wechatInfo.redirect_url || location.href).split('#')[0]),
        response_type: 'code',
        scope: 'snsapi_userinfo',
        state: wechatState,
      },
      { encode: false },
    )}#wechat_redirect`
  }, [wechatInfo, wechatState])

  const wechatSrc = useMemo(() => {
    if (!wechatState || !wechatInfo || wechatInfo.qr_type === 'oa') {
      return ''
    }
    return `https://open.weixin.qq.com/connect/qrconnect?${qs.stringify(
      {
        appid: wechatInfo.app_id,
        redirect_uri: encodeURI((wechatInfo.redirect_url || location.href).split('#')[0]),
        login_type: 'jssdk',
        self_redirect: 'default',
        style: 'black',
        scope: 'snsapi_login',
        state: wechatState,
        href: `${location.origin}/main.css`,
      },
      { encode: false },
    )}`
  }, [wechatInfo, wechatState])

  const getPhoneCode = async (verify: (callback: (p: VerifyRes) => Promise<void>) => void) => {
    if (!currentPhone || phoneCountDown > 0) return
    const requestCode = (p: VerifyRes) =>
      postUserPhoneCode({ phone: currentPhone, kind: 5, ...p }).then(() => {
        startPhone()
      })
    verify(requestCode)
  }

  const getEmailCode = async () => {
    if (!currentMail || emailCountDown > 0) return
    try {
      await getBindMailCode({ mail: currentMail, scene })
      startEmail()
    } catch (error: any) {
      Message.error(error?.message || '验证码发送失败')
    }
  }

  if (!availableTypes.length) {
    return (
      <Stack gap={1.5} sx={{ py: 1.5, px: 2, bgcolor: 'rgba(24, 144, 255, 0.06)', borderRadius: 1.5 }}>
        <Text2 sx={{ color: 'rgba(0, 0, 0, 0.88)' }}>当前账号暂缺可用身份校验方式，暂时无法完成此操作。</Text2>
      </Stack>
    )
  }

  return (
    <Stack gap={2.5}>
      <Stack gap={1}>
        <Text2 sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>身份校验方式</Text2>
        <Tabs
          value={verifyType}
          onChange={(_, value) => {
            onVerifyTypeChange(value)
            setValue('verify_code', '')
            setValue('verify_password', '')
            if (!verifyState) {
              clearWechatState()
            }
          }}
          variant='scrollable'
          scrollButtons='auto'
          sx={{ minHeight: 40 }}
        >
          {availableTypes.map((item) => (
            <Tab key={item} value={item} label={verifyTypeLabelMap[item] || item} sx={{ minHeight: 40, minWidth: 110 }} />
          ))}
        </Tabs>
        <Text2 sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>{verifyTypeHintMap[verifyType]}</Text2>
      </Stack>

      {verifyType === 'phone' && currentPhone && (
        <Controller
          rules={{ required: '请输入验证码' }}
          name='verify_code'
          control={control}
          render={({ field }) => (
            <AuthCodeInput
              required
              {...field}
              placeholder='请输入验证码'
              variant='outlined'
              label='验证码'
              fullWidth
              error={Boolean(errors.verify_code)}
              helperText={(errors.verify_code?.message as string) || `验证码将发送至 ${currentPhone}`}
              size='small'
              autoComplete='off'
              countDown={phoneCountDown}
              getPhoneCode={getPhoneCode}
            />
          )}
        />
      )}

      {verifyType === 'email' && currentMail && (
        <Controller
          rules={{ required: '请输入验证码' }}
          name='verify_code'
          control={control}
          render={({ field }) => (
            <TextField
              required
              {...field}
              variant='outlined'
              placeholder='请输入验证码'
              label='验证码'
              fullWidth
              error={Boolean(errors.verify_code)}
              helperText={(errors.verify_code?.message as string) || `验证码将发送至 ${currentMail}`}
              size='small'
              autoComplete='off'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <Box
                      sx={{
                        color: emailCountDown > 0 ? 'rgba(0,0,0,0.5)' : 'primary.main',
                        fontSize: 14,
                        cursor: 'pointer',
                      }}
                      onClick={getEmailCode}
                    >
                      {emailCountDown && emailCountDown > 0 ? `${emailCountDown}s 后重新获取` : '获取验证码'}
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      )}

      {verifyType === 'password' && (
        <Controller
          rules={{ required: '请输入当前密码' }}
          name='verify_password'
          control={control}
          render={({ field }) => (
            <PasswordInput
              required
              {...field}
              variant='outlined'
              label='当前密码'
              fullWidth
              error={Boolean(errors.verify_password)}
              helperText={errors.verify_password?.message as string}
              size='small'
              autoComplete='off'
            />
          )}
        />
      )}

      {verifyType === 'wechat' && (
        <Stack gap={1.5} alignItems='center' sx={{ py: 1 }}>
          {wechatInfo?.qr_type === 'oa' ? (
            wechatAuthUrl ? (
              <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: 1 }}>
                <ReactQRCode value={wechatAuthUrl} size={180} />
              </Box>
            ) : (
              <Box sx={{ color: 'rgba(0,0,0,0.5)', fontSize: 14 }}>正在加载微信二维码...</Box>
            )
          ) : wechatSrc ? (
            <iframe
              title='微信扫码身份校验'
              style={{ width: '100%', maxWidth: 300, height: 400, border: 0 }}
              src={wechatSrc}
              sandbox='allow-scripts allow-top-navigation'
            />
          ) : (
            <Box sx={{ color: 'rgba(0,0,0,0.5)', fontSize: 14 }}>正在加载微信二维码...</Box>
          )}
          <Text2 sx={{ color: verifyState ? 'success.main' : 'rgba(0, 0, 0, 0.5)' }}>
            {verifyState ? '微信扫码校验已完成' : '扫码成功后将自动完成校验'}
          </Text2>
        </Stack>
      )}
    </Stack>
  )
}

export default SecurityVerifyPanel
